import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../middleware/api';
import { toast } from 'react-toastify';

// Création du contexte
const PopupContext = createContext();

// Hook personnalisé pour utiliser le contexte
export const usePopup = () => useContext(PopupContext);

export const PopupProvider = ({ children }) => {
    const [popups, setPopups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPopup, setCurrentPopup] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [shownPopups, setShownPopups] = useState(new Set());

    // Fonction pour récupérer tous les popups
    const fetchPopups = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get('/popups');
            if (response.data.success) {
                setPopups(response.data.popups);
            } else {
                const message = response.data.message || 'Erreur lors de la récupération des popups';
                setError(message);
                console.error(message);
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Erreur lors de la récupération des popups';
            setError(message);
            console.error('Erreur lors de la récupération des popups :', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fonction pour afficher un popup spécifique
    const showCustomPopup = useCallback(async (currentPopup = false, id) => {
        if (currentPopup) {
            setCurrentPopup(currentPopup);
            setShowPopup(true);
            return;
        }

        if (!id) return;

        try {
            setLoading(true);
            const response = await api.get(`/popups/${id}`);

            if (response.data.success && response.data.popup) {
                setCurrentPopup(response.data.popup);
                setShowPopup(true);
            } else {
                const message = response.data.message || `Popup avec l'ID ${id} non trouvé`;
                console.error(message);
                toast.error(message);
            }
        } catch (error) {
            console.error(`Erreur lors du chargement du popup ID ${id}:`, error);
            toast.error(`Impossible de charger le popup (ID: ${id})`);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fonction pour fermer le popup
    const closePopup = useCallback(() => {
        setShowPopup(false);
        setTimeout(() => {
            setCurrentPopup(null);
        }, 300);
    }, []);

    // Fonction pour marquer un popup comme "ne plus afficher"
    const markPopupAsDoNotShow = useCallback((popupId) => {
        const storageKey = 'verbatim-do-not-show-popup';
        const existingIds = JSON.parse(localStorage.getItem(storageKey) || '[]');

        if (!existingIds.includes(popupId)) {
            const updatedIds = [...existingIds, popupId];
            localStorage.setItem(storageKey, JSON.stringify(updatedIds));
        }
    }, []);

    // Fonction principale pour gérer les popups automatiques
    const handleAutomatedPopup = useCallback(() => {
        // Ne pas déclencher si un popup est déjà affiché
        if (showPopup || currentPopup) {
            return;
        }

        const currentRoute = window.location.pathname;
        
    
        // Filtrer les popups applicables
        const applicablePopups = popups.filter(popup => {
            if (!popup.isActive) return false;
            
            const isValidTime = new Date(popup.startDate) < new Date() && (new Date(popup.endDate) > new Date() || popup.endDate === null);
            if (!isValidTime) return false;
            
            // Vérifier si le popup a été marqué comme "ne plus afficher"
            const doNotShowAgain = localStorage.getItem(`verbatim-do-not-show-popup`);
            if (doNotShowAgain && doNotShowAgain.includes(popup.id.toString())) {
                return false;
            }

            // Vérifier si le popup a déjà été affiché dans cette session
            if (shownPopups.has(popup.id)) {
                return false;
            }

            // Vérifier si le popup s'applique à la route actuelle
            if (popup.routes && Array.isArray(popup.routes)) {
                if (popup.routes.includes('*')) return true;
                
                return popup.routes.some(route => {
                    // Correspondance exacte
                    if (currentRoute === route) return true;
                    
                    // Correspondance pour les routes avec paramètres (ex: /clients/:userId)
                    if (route.includes(':')) {
                        const routePattern = route.replace(/:[^/]+/g, '[^/]+');
                        const regex = new RegExp(`^${routePattern}$`);
                        if (regex.test(currentRoute)) return true;
                    }
                    
                    // Correspondance pour les routes enfants (sauf root)
                    if (route !== '/' && currentRoute.startsWith(route)) return true;
                    
                    return false;
                });
            }
            return false;
        });

        // Trier par type (warning en priorité, puis info)
        const sortedPopups = applicablePopups.sort((a, b) => {
            if (a.type === 'warning' && b.type === 'info') return -1;
            if (a.type === 'info' && b.type === 'warning') return 1;
            return 0;
        });

        // Afficher le premier popup trouvé
        if (sortedPopups.length > 0) {
            const popupToShow = sortedPopups[0];
            showCustomPopup(popupToShow, popupToShow.id);
            setShownPopups(prev => new Set([...prev, popupToShow.id]));
        }
    }, [showPopup, currentPopup, popups, shownPopups, showCustomPopup]);

    const handleShowPopup = (popup) => {
        setCurrentPopup(popup);
        setShowPopup(true);
    };

    // Valeur du contexte
    const value = {
        popups,
        loading,
        error,
        currentPopup,
        showPopup,
        closePopup,
        refreshPopups: fetchPopups,
        setCurrentPopup,
        setShowPopup,
        handleShowPopup,
        showCustomPopup,
        handleAutomatedPopup,
        markPopupAsDoNotShow
    };

    // Charger les popups au montage du composant
    useEffect(() => {
        fetchPopups();
    }, [fetchPopups]);

    // Déclencher les popups après le chargement initial
    useEffect(() => {
        if (popups.length > 0 && !loading) {
            const timeoutId = setTimeout(() => {
                handleAutomatedPopup();
            }, 1500);
            return () => clearTimeout(timeoutId);
        }
    }, [popups, loading, handleAutomatedPopup]);

    return (
        <PopupContext.Provider value={value}>
            {children}
        </PopupContext.Provider>
    );
};

export default PopupContext; 